// src/services/formsService.ts

import {
  FormConfig as BuilderFormConfig,
  FormValidationRules as RulesConfig
} from "../../interfaces/dynamicForms/FormBuilder";
import { PdfMappingDefinition } from "../../interfaces/dynamicForms/MappingTypes";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL; // Your API base URL

/**
 * Saves (creates or updates) a form configuration on the backend.
 * Invalidates relevant caches upon successful submission.
 *
 * @param formConfig - The complete form configuration object from the builder (containing definition and rules).
 * @param authToken - The user's authentication token.
 * @throws {Error} If the API request fails.
 */
export async function saveFormConfiguration(
  formConfig: BuilderFormConfig, // Use the type from the builder state
  mappingConfig: PdfMappingDefinition,
  authToken: string
): Promise<void> { // Assuming API returns no specific data on success, otherwise adjust return type

  // ***** CONFIRM OR CHANGE THIS API ENDPOINT *****
  const apiUrl = `${API_BASE_URL}/form/config`;
  console.log(`Attempting to save form configuration to: ${apiUrl}`);

  // Prepare the payload structure expected by the backend
  // This structure matches the one discussed previously
  const payload = {
    formDefinition: { [formConfig.definition.formId]: formConfig.definition },
    validationRules: { [formConfig.rules.formId]: formConfig.rules },
    mappingRules: { [formConfig.definition.formId]: mappingConfig }
  };

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorDetails = response.statusText;
      try {
        const errorBody = await response.json();
        errorDetails = errorBody?.message || JSON.stringify(errorBody) || errorDetails;
        console.error("API Error Response Body:", errorBody);
      } catch (e) {
        // If response body isn't JSON or reading fails, use status text
        const textError = await response.text(); // Get raw text if JSON fails
        console.error("API Error Response Text:", textError);
        errorDetails = textError || errorDetails;
      }
      throw new Error(`Failed to save form configuration: ${response.status} - ${errorDetails}`);
    }

    // Success
    console.log(`Form configuration for '${formConfig.definition.formId}' saved successfully.`);

    // --- Cache Invalidation ---
    // Remove potentially stale cache entries after a successful save
    try {
      localStorage.removeItem("formConfigsCache");
      localStorage.removeItem("formRulesCache");
      console.log("Cleared formConfigsCache and formRulesCache after successful save.");
    } catch (cacheError) {
      console.error("Error removing cache after save:", cacheError);
      // Non-fatal, proceed even if cache removal fails
    }

  } catch (error) {
    console.error("Error in saveFormConfiguration:", error);
    // Re-throw the error to be caught by the calling component (FormBuilder)
    throw error;
  }
}

export const getMappingConfig = async (formId: string, authToken: string): Promise<any> => {
  const response = await fetch(`${API_BASE_URL}/form/specificConfig/MAPPING/${formId}`, {
    headers: { 'Authorization': `Bearer ${authToken}` },
  });
  if (!response.ok) {
    if (response.status === 404) return null; // Not found is okay, means no mapping exists yet
    throw new Error(`Failed to fetch mapping config for ${formId}`);
  }
  return response.json();
};

/**
 * Fetches the active PDF template for a specific form.
 * @returns A File object or null if not found.
 */
export const getPdfTemplate = async (formId: string, authToken: string): Promise<File | null> => {
  const response = await fetch(`${API_BASE_URL}/form/template/${formId}`, {
    headers: { 'Authorization': `Bearer ${authToken}` },
  });

  if (!response.ok) {
    if (response.status === 404) return null; // Not found is an expected case.
    // For other errors, try to get more details.
    const errorText = await response.text();
    throw new Error(`Failed to fetch PDF template for ${formId}. Status: ${response.status}. Details: ${errorText}`);
  }

  // NEW: Check the content type before processing the blob
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/pdf')) {
    console.error("Expected a PDF file but received content type:", contentType);
    throw new Error(`Server did not return a PDF file. Received: ${contentType}`);
  }

  const blob = await response.blob();
  const fileName = response.headers.get('content-disposition')?.split('filename=')[1] || `${formId}_template.pdf`;
  return new File([blob], fileName, { type: 'application/pdf' });
};
/**
 * Uploads a new PDF template for a form.
 */
export const uploadPdfTemplate = async (formId: string, file: File, authToken: string): Promise<void> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/form/template/${formId}`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${authToken}` },
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Failed to upload PDF template for ${formId}`);
  }
};

/**
 * Fetches a specific configuration part (CONFIG, RULES, or MAPPING) for a single form.
 */
export const getSpecificConfig = async (
  formId: string,
  type: 'CONFIG' | 'RULES' | 'MAPPING',
  authToken: string
): Promise<any> => {
  // Note: Ensure your API route matches this pattern.
  const response = await fetch(`${API_BASE_URL}/form/specificConfig/${type}/${encodeURIComponent(formId)}`, {
    headers: { 'Authorization': `Bearer ${authToken}` },
  });
  if (!response.ok) {
    if (response.status === 404) return null; // A 404 is a valid case (e.g., no rules exist yet)
    throw new Error(`Failed to fetch ${type} config for ${formId}`);
  }
  return response.json();
};

/**
 * Saves a single configuration file by reusing the main config endpoint.
 * This function is the corrected version to fix the 405 error.
 */
// You can name this whatever you like (e.g., saveAdminFormConfiguration)
export async function saveSpecificForm(
    formConfig: BuilderFormConfig,
    mappingConfig: PdfMappingDefinition,
    authToken: string
): Promise<void> {

  const apiUrl = `${API_BASE_URL}/form/config`;
  const formId = formConfig.definition.formId;

  // THIS IS THE CORRECT PAYLOAD STRUCTURE FOR YOUR BACKEND
  // It wraps each configuration object with the formId as the key.
  const payload = {
    formDefinition: { [formId]: formConfig.definition },
    validationRules: { [formId]: formConfig.rules },
    mappingRules: { [formId]: mappingConfig }
  };

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorDetails = await response.text();
      // The error message you received comes from here
      throw new Error(`Failed to save form configuration: ${errorDetails}`);
    }

    // ... your success and cache logic
    console.log(`Form configuration for '${formId}' saved successfully.`);
    localStorage.removeItem("formConfigsCache");
    localStorage.removeItem("formRulesCache");

  } catch (error) {
    console.error("Error in saveSpecificForm:", error);
    throw error;
  }
}